import React, { useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Skeleton } from "antd";
import ChartTooltip from "./ChartTooltip";
import { STATUS_CRITICAL, TEXT_MUTED, GRIDLINE } from "./palette";
import { labelAmountBuckets } from "./histogram";

const EmptyNote = styled.div`
  color: ${TEXT_MUTED};
`;

function HistogramTooltip({ active, payload }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const { label, count } = payload[0].payload;

  return <ChartTooltip label={label} value={`${count} 筆`} />;
}

HistogramTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.object),
};

HistogramTooltip.defaultProps = {
  active: false,
  payload: [],
};

export default function AmountHistogram({ buckets, loading, barColor }) {
  const labeledBuckets = useMemo(() => labelAmountBuckets(buckets), [buckets]);

  if (loading && !buckets) {
    return <Skeleton active title={false} paragraph={{ rows: 3 }} />;
  }

  if (labeledBuckets.length === 0) {
    return <EmptyNote>沒有足夠的資料可以呈現金額分布。</EmptyNote>;
  }

  return (
    <ResponsiveContainer width="100%" height={160}>
      <BarChart data={labeledBuckets} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid vertical={false} stroke={GRIDLINE} />
        <XAxis
          dataKey="label"
          tick={{ fill: TEXT_MUTED, fontSize: 11 }}
          axisLine={{ stroke: GRIDLINE }}
          tickLine={false}
        />
        <YAxis
          allowDecimals={false}
          tick={{ fill: TEXT_MUTED, fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          width={32}
        />
        <Tooltip content={<HistogramTooltip />} cursor={{ fill: "rgba(11,11,11,0.04)" }} />
        <Bar dataKey="count" fill={barColor} radius={[4, 4, 0, 0]} maxBarSize={36} />
      </BarChart>
    </ResponsiveContainer>
  );
}

AmountHistogram.propTypes = {
  buckets: PropTypes.arrayOf(
    PropTypes.shape({
      rangeStart: PropTypes.number,
      rangeEnd: PropTypes.number,
      count: PropTypes.number,
    })
  ),
  loading: PropTypes.bool,
  barColor: PropTypes.string,
};

AmountHistogram.defaultProps = {
  buckets: null,
  loading: false,
  barColor: STATUS_CRITICAL,
};
